import React from 'react';
import { Box, Button, Popover, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Warning as WarningIcon } from '@mui/icons-material';

export interface TypeMismatchPopoverProps {
  /** Anchor element for the popover. */
  anchorEl: HTMLElement | null;
  /** Whether the popover is open. */
  open: boolean;
  /** Close handler. */
  onClose: () => void;
  /** Source data type. */
  sourceType: string;
  /** Target data type. */
  targetType: string;
  /** Source field name. */
  sourceField: string;
  /** Target field name. */
  targetField: string;
  /** Apply CAST expression handler. */
  onApplyCast?: () => void;
  /** Proceed without CAST. */
  onProceed?: () => void;
}

/**
 * Phase 3.2: Popover shown after drag-to-connect when source and target
 * field types differ. Offers to insert a CAST expression.
 */
const TypeMismatchPopover: React.FC<TypeMismatchPopoverProps> = ({
  anchorEl,
  open,
  onClose,
  sourceType,
  targetType,
  sourceField,
  targetField,
  onApplyCast,
  onProceed,
}) => {
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      PaperProps={{
        sx: {
          p: 2,
          maxWidth: 340,
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(15,23,42,0.14)',
        },
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <WarningIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 13 }}>
            Type Mismatch Detected
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1,
            borderRadius: 1.5,
            backgroundColor: alpha('#f59e0b', 0.06),
            border: `1px solid ${alpha('#f59e0b', 0.2)}`,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
              Source
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12 }}>
              {sourceField}
            </Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#2563eb', fontSize: 11 }}>
              {sourceType}
            </Typography>
          </Box>
          <Typography sx={{ color: 'text.disabled', mx: 1 }}>→</Typography>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
              Target
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12 }}>
              {targetField}
            </Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#0891b2', fontSize: 11 }}>
              {targetType}
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            size="small"
            variant="outlined"
            onClick={() => { onProceed?.(); onClose(); }}
            sx={{ fontSize: 12, textTransform: 'none' }}
          >
            Keep as-is
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => { onApplyCast?.(); onClose(); }}
            sx={{ fontSize: 12, textTransform: 'none' }}
          >
            Apply CAST
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
};

export default TypeMismatchPopover;
